import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Box, Flex, Input, InputGroup, InputLeftElement, useMediaQuery, useOutsideClick, useStyleConfig } from '@chakra-ui/react';
import { CountryCode, DATE_TYPE, FT_PI_TWO_MONTH_SEARCH } from '@whitbread-eos/api';
import { getCookie, isSameDate, TWO_MONTH_SEARCH, useFeatureToggle } from '@whitbread-eos/utils';
import classNames from 'classnames';
import { add, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns';
import { de, enGB } from 'date-fns/locale';
import { nanoid } from 'nanoid';
import { useEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import Calendar from '../../assets/icons/Calendar';
import DatePickerGlobalStyles from '../../theme/components/DatePickerGlobalStyles';
import Button from '../Button';
import Icon from '../Icon';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function handleSameDateAsToday(endDate, tomorrow, dateFormat, labels, locale) {
  if (endDate === null) {
    return "".concat(labels === null || labels === void 0 ? void 0 : labels.todayLabel, " | ").concat(labels === null || labels === void 0 ? void 0 : labels.checkoutLabel);
  }
  if (isSameDate(endDate, tomorrow)) {
    return "".concat(labels === null || labels === void 0 ? void 0 : labels.todayLabel, " | ").concat(labels === null || labels === void 0 ? void 0 : labels.tomorrowLabel);
  }
  return "".concat(labels === null || labels === void 0 ? void 0 : labels.todayLabel, " | ").concat(format(endDate, dateFormat, locale === CountryCode.DE ? {
    locale: de
  } : {
    locale: enGB
  }));
}
export function getFormattedDate(startDate, endDate, dateFormat, locale, props) {
  var labels = props.labels,
    hasDatepickerRange = props.selectsRange,
    isError = props.isError,
    displayDatesNotification = props.displayDatesNotification;
  var today = new Date();
  var tomorrow = add(today, {
    days: 1
  });
  var isLocaleGerman = locale === CountryCode.DE;
  var localeOption = {
    locale: isLocaleGerman ? de : enGB
  };
  dateFormat = isLocaleGerman ? DATE_TYPE.E_DAY_MONTH_YEAR : dateFormat;
  nanoid();
  if (hasDatepickerRange) {
    if (startDate === null && endDate === null) {
      if (isError) return '';
      if (displayDatesNotification) {
        return "".concat(labels === null || labels === void 0 ? void 0 : labels.checkInLabel, " | ").concat(labels === null || labels === void 0 ? void 0 : labels.checkoutLabel);
      } else {
        return "".concat(labels === null || labels === void 0 ? void 0 : labels.todayLabel, " | ").concat(labels === null || labels === void 0 ? void 0 : labels.tomorrowLabel);
      }
    }
    if (isSameDate(startDate, today)) {
      return handleSameDateAsToday(endDate, tomorrow, dateFormat, labels, locale);
    }
    if (isSameDate(startDate, tomorrow)) {
      if (endDate === null) {
        return "".concat(labels === null || labels === void 0 ? void 0 : labels.tomorrowLabel, " | ").concat(labels === null || labels === void 0 ? void 0 : labels.checkoutLabel);
      }
      return "".concat(labels === null || labels === void 0 ? void 0 : labels.tomorrowLabel, " | ").concat(format(endDate, dateFormat, localeOption));
    }
    if (startDate && endDate === null) {
      return "".concat(format(startDate, dateFormat, localeOption), " | ").concat(labels === null || labels === void 0 ? void 0 : labels.checkoutLabel);
    }
    return "".concat(format(startDate, dateFormat, localeOption), " | ").concat(format(endDate, dateFormat, localeOption));
  }
  return startDate ? format(startDate, dateFormat, localeOption) : '';
}
export default function Datepicker(props) {
  var _props$hasFooter;
  var defaultStartDate = props.defaultStartDate,
    defaultEndDate = props.defaultEndDate,
    isDatePickerFocus = props.isDatePickerFocus;
  var baseDatepickerStyles = useStyleConfig('Datepicker');
  var hasFooter = (_props$hasFooter = props.hasFooter) !== null && _props$hasFooter !== void 0 ? _props$hasFooter : false;
  var _useState = useState(defaultStartDate !== null && defaultStartDate !== void 0 ? defaultStartDate : null),
    _useState2 = _slicedToArray(_useState, 2),
    startDate = _useState2[0],
    setStartDate = _useState2[1];
  var _useState3 = useState(defaultEndDate !== null && defaultEndDate !== void 0 ? defaultEndDate : null),
    _useState4 = _slicedToArray(_useState3, 2),
    endDate = _useState4[0],
    setEndDate = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    calendarKey = _useState6[0],
    setCalendarKey = _useState6[1];
  var ref = useRef(null);
  var refInput = useRef(null);
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isCalendarOpen = _useState8[0],
    setIsCalendarOpen = _useState8[1];
  var _getDatepickerStyles = getDatepickerStyles(isCalendarOpen, props.datepickerStyles),
    inputStyles = _getDatepickerStyles.inputStyles,
    inputIconStyles = _getDatepickerStyles.inputIconStyles,
    resetButtonStyles = _getDatepickerStyles.resetButtonStyles,
    doneButtonStyles = _getDatepickerStyles.doneButtonStyles;
  var _useMediaQuery = useMediaQuery('(min-width: 1280px)'),
    _useMediaQuery2 = _slicedToArray(_useMediaQuery, 1),
    isDesktop = _useMediaQuery2[0];
  var _useMediaQuery3 = useMediaQuery('(pointer: coarse)'),
    _useMediaQuery4 = _slicedToArray(_useMediaQuery3, 1),
    isTouchDevice = _useMediaQuery4[0];
  var _useFeatureToggle = useFeatureToggle(),
    isTwoMonthSearchEnabled = _useFeatureToggle[FT_PI_TWO_MONTH_SEARCH];
  var showTwoMonths = isTwoMonthSearchEnabled && getCookie(TWO_MONTH_SEARCH) === 'true' && isDesktop;
  useOutsideClick({
    ref: ref,
    handler: () => setIsCalendarOpen(false)
  });
  registerLocale(CountryCode.DE, de);
  useEffect(() => {
    if (defaultStartDate !== undefined) setStartDate(defaultStartDate);
    if (defaultEndDate !== undefined) setEndDate(defaultEndDate);
  }, [defaultStartDate, defaultEndDate]);
  useEffect(() => {
    var _refInput$current;
    isDatePickerFocus && (refInput === null || refInput === void 0 || (_refInput$current = refInput.current) === null || _refInput$current === void 0 ? void 0 : _refInput$current.focus());
  }, [isDatePickerFocus]);
  return _jsxs(Box, {
    __css: baseDatepickerStyles,
    ref: ref,
    onKeyDown: event => {
      if (event.key === 'Escape' && isCalendarOpen) {
        var _refInput$current2;
        setIsCalendarOpen(false);
        (_refInput$current2 = refInput.current) === null || _refInput$current2 === void 0 || _refInput$current2.focus();
      }
    },
    children: [_jsx(DatePickerGlobalStyles, {}), _jsx(DatePicker, _objectSpread(_objectSpread(_objectSpread({}, props.locale === CountryCode.DE ? {
      locale: CountryCode.DE
    } : {
      locale: CountryCode.EN
    }), {}, {
      selected: startDate,
      startDate: startDate,
      endDate: endDate,
      onChange: handleDatepickerChange,
      onKeyDown: event => {
        if (event.key === 'Enter') {
          handleDatepickerChange([startDate, endDate]);
        }
      },
      customInput: renderInput(),
      minDate: props.minDate,
      maxDate: props === null || props === void 0 ? void 0 : props.maxDate,
      useWeekdaysShort: true,
      calendarStartDay: 1,
      disabled: props.isDisabled,
      open: isCalendarOpen
    }, props.selectsRange && {
      selectsRange: true
    }), {}, {
      renderDayContents: renderDayContents,
      dayClassName: getDayClassName,
      monthsShown: showTwoMonths ? 2 : 1,
      popperPlacement: 'bottom-start',
      popperClassName: showTwoMonths ? 'two-month-calendar' : '',
      popperProps: props.disableFlip ? {
        strategy: 'fixed',
        middleware: [{
          name: 'offset',
          fn: s => ({
            x: s.x,
            y: s.y + 10
          })
        }]
      } : undefined,
      children: hasFooter && renderCalendarFooter()
    }), calendarKey)]
  });
  function getDayClassName(date) {
    var startWeek = startOfWeek(date, {
      weekStartsOn: 1
    });
    var endWeek = endOfWeek(date, {
      weekStartsOn: 1
    });
    var startMonth = startOfMonth(date);
    var endMonth = endOfMonth(date);
    var arr = [{
      'start-week': startWeek.getDate() === date.getDate()
    }, {
      'end-week': endWeek.getDate() === date.getDate()
    }, {
      'start-month': startMonth.getDate() === date.getDate()
    }, {
      'end-month': endMonth.getDate() === date.getDate()
    }, {
      'react-datepicker__day--disabled': isDayDisabled(date)
    }];
    return classNames(arr);
  }
  function isDayDisabled(date) {
    if (!startDate) return false;
    if (props.selectsRange && !endDate) {
      return resetDate(date) < resetDate(startDate);
    }
    return resetDate(date) < resetDate(props.minDate);
  }
  function renderInput() {
    var _props$displayDateFor, _props$datepickerStyl;
    var displayDateFormat = (_props$displayDateFor = props.displayDateFormat) !== null && _props$displayDateFor !== void 0 ? _props$displayDateFor : props.dateFormat;
    var borderStyles = isCalendarOpen ? {
      borderColor: 'primary'
    } : {};
    var locale = props.locale === CountryCode.DE ? CountryCode.DE : CountryCode.EN;
    var value = getFormattedDate(startDate, endDate, displayDateFormat, locale, props);
    return _jsx(InputGroup, _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, _objectSpread(_objectSpread({}, props.datepickerStyles.inputGroupStyles), {}, {
      minW: '0',
      border: {
        base: "".concat(isCalendarOpen ? '0' : '1px solid var(--chakra-colors-lightGrey1)'),
        sm: '0'
      }
    })), (_props$datepickerStyl = props.datepickerStyles) === null || _props$datepickerStyl === void 0 ? void 0 : _props$datepickerStyl.errorInputGroupStyles), borderStyles), {}, {
      children: _jsxs(Flex, {
        w: "full",
        children: [_jsx(InputLeftElement, _objectSpread(_objectSpread({
          pointerEvents: "none"
        }, inputIconStyles), {}, {
          children: _jsx(Icon, {
            svg: _jsx(Calendar, {})
          })
        })), _jsx(Input, _objectSpread(_objectSpread({
          "aria-label": "datepicker-input",
          placeholder: props.inputPlaceholder,
          value: value,
          onChange: event => {
            var _props$onInputChange;
            (_props$onInputChange = props.onInputChange) === null || _props$onInputChange === void 0 || _props$onInputChange.call(props, event.target.value);
          },
          isDisabled: props.isDisabled,
          onClick: () => {
            setIsCalendarOpen(true);
          },
          onKeyDown: event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              setIsCalendarOpen(true);
            }
            if (event.altKey && event.key === 'ArrowDown') {
              event.preventDefault();
              setIsCalendarOpen(true);
            }
          }
        }, inputStyles), {}, {
          ref: refInput
        }))]
      })
    }));
  }
  function renderCalendarFooter() {
    var _props$labels, _props$labels2;
    return _jsxs(Box, {
      textAlign: "center",
      mb: "xl",
      float: "left",
      w: "full",
      children: [_jsx(Button, _objectSpread(_objectSpread({
        size: "sm",
        variant: "tertiary",
        onClick: handleReset,
        isDisabled: startDate === null,
        tabIndex: startDate !== null ? 0 : -1
      }, resetButtonStyles), {}, {
        children: (_props$labels = props.labels) === null || _props$labels === void 0 ? void 0 : _props$labels.resetButtonLabel
      })), _jsx(Button, _objectSpread(_objectSpread({
        size: "sm",
        variant: "tertiary",
        onClick: handleDone
      }, doneButtonStyles), {}, {
        tabIndex: 0,
        children: (_props$labels2 = props.labels) === null || _props$labels2 === void 0 ? void 0 : _props$labels2.doneButtonLabel
      }))]
    });
  }
  function renderDayContents(day) {
    return _jsxs(_Fragment, {
      children: [_jsx("div", {
        className: "highlight"
      }), _jsx("div", {
        className: "day-number",
        tabIndex: isTouchDevice ? undefined : new Date().getDate() === day ? 0 : -1,
        children: day
      })]
    });
  }
  function handleDone() {
    var _props$onDone;
    setIsCalendarOpen(false);
    (_props$onDone = props.onDone) === null || _props$onDone === void 0 || _props$onDone.call(props, [startDate, endDate]);
  }
  function handleReset() {
    var _props$onReset;
    var today = new Date();
    if (props.selectsRange) {
      var tomorrow = add(today, {
        days: 1
      });
      setStartDate(today);
      setEndDate(tomorrow);
    } else {
      setStartDate(today);
      setEndDate(null);
    }
    setCalendarKey(prev => prev + 1);
    (_props$onReset = props.onReset) === null || _props$onReset === void 0 || _props$onReset.call(props);
  }
  function handleDatepickerChange(dates) {
    var _props$onSelectDates2;
    if (props.selectsRange) {
      var _props$onSelectDates;
      var _ref = dates,
        _ref2 = _slicedToArray(_ref, 2),
        start = _ref2[0],
        end = _ref2[1];
      setStartDate(start);
      if (isSameDate(start, end)) return;
      setEndDate(end);
      (_props$onSelectDates = props.onSelectDates) === null || _props$onSelectDates === void 0 || _props$onSelectDates.call(props, [start, end]);
      return;
    }
    setStartDate(dates);
    (_props$onSelectDates2 = props.onSelectDates) === null || _props$onSelectDates2 === void 0 || _props$onSelectDates2.call(props, dates);
    if (props.closeCalendarOnSelectDate) {
      setIsCalendarOpen(false);
    }
  }
}
function getDatepickerStyles(isCalendarOpen, datepickerStyles) {
  var inputStyles = _objectSpread(_objectSpread({
    padding: 'var(--chakra-space-lg) var(--chakra-space-lg) var(--chakra-space-lg) var(--chakra-space-2xl)',
    outline: 'none',
    color: 'var(--chakra-colors-darkGrey1)',
    cursor: 'pointer',
    border: isCalendarOpen ? '0.125rem solid var(--chakra-colors-primary)' : '0.125rem solid transparent',
    borderRadius: isCalendarOpen ? 'var(--chakra-space-xs)' : 0,
    borderColor: isCalendarOpen ? 'var(--chakra-colors-primary)' : 'transparent',
    borderRight: {
      base: "".concat(isCalendarOpen ? '0.125rem solid var(--chakra-colors-primary)' : '0'),
      sm: "".concat(isCalendarOpen ? '0.125rem solid var(--chakra-colors-primary)' : '0.063rem solid var(--chakra-colors-lightGrey4)')
    },
    borderTopRightRadius: isCalendarOpen ? 'var(--chakra-space-xs)' : 0,
    borderBottomRightRadius: isCalendarOpen ? 'var(--chakra-space-xs)' : 0,
    textOverflow: 'ellipsis',
    marginLeft: {
      mobile: '-1px',
      xs: '-1px',
      sm: 0
    },
    _hover: isCalendarOpen ? {
      border: '0.125rem solid var(--chakra-colors-primary)',
      borderRadius: 'var(--chakra-space-xs)',
      borderColor: 'var(--chakra-colors-primary)'
    } : {
      border: '0.063rem solid var(--chakra-colors-darkGrey1)',
      borderRadius: 'var(--chakra-space-xs)',
      borderColor: 'var(--chakra-colors-darkGrey1)'
    },
    _active: {
      border: '0.125rem solid var(--chakra-colors-primary)',
      borderRadius: 'var(--chakra-space-xs)',
      borderColor: 'var(--chakra-colors-primary)'
    },
    _focus: {
      border: '0.125rem solid var(--chakra-colors-primary)',
      borderRadius: 'var(--chakra-space-xs)',
      borderColor: 'var(--chakra-colors-primary)'
    },
    _disabled: {
      border: 'none'
    }
  }, datepickerStyles.datepickerInputElementStyles), datepickerStyles === null || datepickerStyles === void 0 ? void 0 : datepickerStyles.errorInputElementStyles);
  var inputIconStyles = _objectSpread({
    top: 'var(--chakra-space-md)'
  }, datepickerStyles.iconStyles);
  var responsiveButtonWidth = {
    mobile: '7.7rem',
    xs: '9.0625rem',
    sm: '9.25rem'
  };
  var resetButtonStyles = {
    mr: 'lg',
    border: 'none',
    boxShadow: 'none',
    _hover: {
      bg: 'baseWhite',
      boxShadow: 'none',
      textDecoration: 'underline'
    },
    _disabled: {
      color: 'lightGrey2',
      cursor: 'default',
      _hover: {
        textDecoration: 'none'
      }
    },
    w: responsiveButtonWidth
  };
  var doneButtonStyles = {
    w: responsiveButtonWidth,
    _hover: {
      bg: 'baseWhite'
    }
  };
  return {
    inputStyles,
    inputIconStyles,
    resetButtonStyles,
    doneButtonStyles
  };
}
function resetDate(date) {
  return date.setHours(12, 0, 0, 0);
}